import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Checkbox, Spin } from 'antd';

import { useMutation } from '@tanstack/react-query';
import { formatDate } from 'helpers/date';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML';
import PermissionHide from 'v2/components/Shared/PermissionHide/index';
import { messagingService } from 'v2/services/messaging';

import { useSelectedChat } from '../../useSelectedChat';

interface IProps {
  chatId: number;
}

const MessagesChat: React.FC<IProps> = ({ chatId }) => {
  const [valueAnswer, setValueAnswer] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [shiftDown, setShiftDown] = useState(false);

  const shiftDownRef = useRef(shiftDown);
  shiftDownRef.current = shiftDown;

  const { messages, isLoading, isFetching, refetch } = useSelectedChat(chatId);

  const sendMessageMutation = useMutation({
    mutationFn: (message: { content: string }) => messagingService.sendMessageChat(chatId, message.content, sendEmail),
    onSettled: () => {
      setValueAnswer('');
      refetch();
    }
  });

  const scrollToBottom = useCallback(() => {
    const element = document.getElementById('chat-content');

    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  const handleSendMessage = useCallback(async () => {
    sendMessageMutation.mutate({ content: valueAnswer });
  }, [valueAnswer, sendMessageMutation]);

  const handleChangeFieldResponse = useCallback((e: any) => {
    setValueAnswer(e.target.value);
  }, []);

  const handleChangeSendEmail = useCallback((e: any) => {
    setSendEmail(e.target.checked);
  }, []);

  const handlePressEnter = useCallback(
    (e: any) => {
      if (e.keyCode === 16) {
        setShiftDown(true);
      }

      if (e.key === 'Enter' && !shiftDownRef.current) {
        e.preventDefault();
        setValueAnswer('');
        handleSendMessage();
        scrollToBottom();
      }
    },
    [handleSendMessage, scrollToBottom]
  );

  const handleShiftRelease = useCallback((e: any) => {
    if (e.keyCode === 16) {
      setShiftDown(false);
    }
  }, []);

  return (
    <div className='bg-surface-default h-full rounded-sm p-4 shadow-sm'>
      <div className='relative max-h-[64vh] min-h-[64vh]'>
        <div
          className='bg-fill-secondary relative max-h-[64vh] min-h-[64vh] w-full overflow-auto rounded-sm p-5'
          id='chat-content'
        >
          {!isLoading &&
            chatId &&
            messages.map((message: any, index: any) => {
              return (
                <div
                  key={'chat-message-' + index}
                  className={`clear-both mb-3 w-auto max-w-75 rounded-sm text-sm leading-4 font-normal text-white ${
                    message.is_self ? 'float-right' : ''
                  } last:mb-0`}
                >
                  <div className='text-content-subtitle block w-full text-[[0.8125rem]'>
                    {message.is_self ? message.self?.name : message.outer?.name ? message.outer.name : ''}
                  </div>

                  <NoDangerouslyHTML
                    className={`relative inline-block w-auto max-w-full rounded-1.25 px-4 py-2 font-normal wrap-break-word ${
                      message.is_self ? 'float-right mb-1 bg-(--uizz-antd-color-success)/20' : 'bg-info/20'
                    }`}
                    html={message.content ?? 'Infelizmente não conseguimos recuperar o conteúdo desta mensagem.'}
                  />

                  <div
                    className={`text-content-subtitle clear-both mt-1 w-full text-xs ${message.is_self ? 'mt-0 text-right' : ''}`}
                  >
                    {message.created_at && formatDate(message.created_at, 'dd/MM/yyyy H:mm')}
                  </div>
                </div>
              );
            })}

          {chatId === 0 && (
            <div className='absolute top-0 left-0 flex size-full items-center justify-center text-center text-sm'>
              Selecione uma conversa para visualizar as mensagens.
            </div>
          )}

          {isLoading && chatId > 0 && (
            <div className='absolute top-0 left-0 flex size-full items-center justify-center text-center'>
              <Spin />
            </div>
          )}
        </div>

        {!isLoading && isFetching && chatId > 0 && (
          <div className='absolute bottom-2 left-0 flex w-full items-center justify-center [&_small]:ml-2 [&_small]:text-[[0.6875rem]'>
            <Spin size='small' />
            <small>Buscando novas mensagens...</small>
          </div>
        )}
      </div>

      <div>
        <textarea
          className='border-outline-default text-content-subtitle min-h-15 w-full resize-none rounded-sm border px-3 py-2.5 text-[[0.9375rem] leading-4.75 font-normal transition-colors'
          placeholder='Escreva sua mensagem'
          onChange={handleChangeFieldResponse}
          value={valueAnswer}
          onKeyDown={handlePressEnter}
          onKeyUp={handleShiftRelease}
          disabled={chatId === 0 || isLoading}
        />

        <div className='flex justify-between text-[[0.8125rem]'>
          <div>
            <Checkbox
              checked={sendEmail}
              onChange={handleChangeSendEmail}
              disabled={(chatId === 0 && true) || isLoading}
            />{' '}
            Enviar cópia por e-mail
          </div>

          <div>
            <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
              <Button
                type='primary'
                disabled={sendMessageMutation.isPending || isLoading || chatId === 0 || !valueAnswer.length}
                onClick={handleSendMessage}
                loading={sendMessageMutation.isPending}
              >
                Enviar
              </Button>
            </PermissionHide>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesChat;
